import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Chip,
  Tabs,
  Tab,
  Tooltip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import {
  RateReview as RateReviewIcon,
  CheckCircle as CheckIcon,
  Cancel as WrongIcon,
  HelpOutline as HelpIcon,
  AutoAwesome as AiIcon,
  Lightbulb as HintIcon,
  TrendingUp as TrendingUpIcon,
  ExpandMore as ExpandMoreIcon,
} from "@mui/icons-material";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from "recharts";
import api, { getPastPaperApiPrefix, getPastPaperPortalHomePath, getPastPaperResultPath } from "../../../api";
import useExamMode from "../../../hooks/useExamMode";
import HtmlWithLatex from "../../QuestionBank/components/HtmlWithLatex";
import MarksBreakdownTable from "../components/MarksBreakdownTable";
import { resolveLeafVerdict } from "../utils/leafMarkVerdict";
import { fmtMarks, truncateHtml, formatAnswerLabel } from "../utils/breakdownFormat";

const STUDENT_BASE = process.env.REACT_APP_STUDENT_PREFIX || "student";
const PIE_COLORS = ["#4caf50", "#f59e0b", "#ef5350", "#9e9e9e"];

const PERFORMANCE_LEVEL_CONFIG = {
  excellent: { label: "Excellent", bgcolor: "#dcfce7", color: "#166534" },
  good: { label: "Good", bgcolor: "#dbeafe", color: "#1e40af" },
  average: { label: "Average", bgcolor: "#fef9c3", color: "#854d0e" },
  needs_improvement: { label: "Needs Improvement", bgcolor: "#fee2e2", color: "#991b1b" },
  unknown: { label: "Unknown", bgcolor: "#f3f4f6", color: "#374151" },
};

const derivePerformanceLevel = (pct) => {
  if (pct >= 80) return "excellent";
  if (pct >= 60) return "good";
  if (pct >= 40) return "average";
  return "needs_improvement";
};

const AiInsightsTab = ({ aiFeedbackStatus, aiOverall, aiScore, aiPercentage, totalMarks, ruleScore }) => {
  if (aiFeedbackStatus === "pending" || aiFeedbackStatus === "processing") {
    return (
      <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", py: 8, gap: 2 }}>
        <CircularProgress size={44} sx={{ color: "#7c3aed" }} />
        <Typography variant="body1" sx={{ color: "#7c3aed", fontWeight: 600 }}>
          AI is analysing your answers…
        </Typography>
        <Typography variant="body2" color="text.secondary">
          This usually takes 5–15 seconds. The page will update automatically.
        </Typography>
      </Box>
    );
  }

  if (aiFeedbackStatus === "failed" || !aiOverall) {
    return (
      <Alert severity="warning" sx={{ mt: 2 }}>
        AI feedback is not available for this attempt. This may be due to a temporary issue.
      </Alert>
    );
  }

  const resolvedPct = Number(aiPercentage ?? (totalMarks > 0 ? (aiScore / totalMarks) * 100 : 0));
  const resolvedLevel = aiPercentage != null || aiScore != null
    ? derivePerformanceLevel(resolvedPct)
    : (aiOverall.performance_level ?? "unknown");
  const perfConfig = PERFORMANCE_LEVEL_CONFIG[resolvedLevel] ?? PERFORMANCE_LEVEL_CONFIG.unknown;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 2 }}>
        <Card variant="outlined" sx={{ borderColor: "#e0d7ff", bgcolor: "#f3f0ff" }}>
          <CardContent sx={{ pb: "16px !important" }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
              <AiIcon sx={{ color: "#7c3aed", fontSize: 18 }} />
              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "#7c3aed", textTransform: "uppercase", letterSpacing: 0.5, fontSize: "0.7rem" }}>
                AI-Adjusted Score
              </Typography>
            </Box>
            <Box sx={{ display: "flex", alignItems: "baseline", gap: 0.5, mb: 0.5 }}>
              <Typography variant="h3" sx={{ fontWeight: 800, color: "#7c3aed", lineHeight: 1 }}>
                {Number(aiScore ?? 0).toFixed(1)}
              </Typography>
              <Typography variant="h6" sx={{ color: "text.secondary", fontWeight: 400 }}>
                / {Number(totalMarks ?? 0).toFixed(1)}
              </Typography>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.75 }}>
              <Typography variant="body1" sx={{ color: "#7c3aed", fontWeight: 700 }}>
                {resolvedPct.toFixed(1)}%
              </Typography>
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.75 }}>
              Based on semantic answer matching
            </Typography>
          </CardContent>
        </Card>

        <Card variant="outlined" sx={{ borderColor: perfConfig.bgcolor !== "#f3f4f6" ? perfConfig.bgcolor : "divider" }}>
          <CardContent sx={{ pb: "16px !important" }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
              <TrendingUpIcon sx={{ color: "text.secondary", fontSize: 18 }} />
              <Typography variant="subtitle2" sx={{ fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5, fontSize: "0.7rem", color: "text.secondary" }}>
                Performance Level
              </Typography>
            </Box>
            <Box sx={{ display: "inline-flex", alignItems: "center", px: 2, py: 0.75, borderRadius: 2, bgcolor: perfConfig.bgcolor, mb: 1.5 }}>
              <Typography variant="h6" sx={{ fontWeight: 800, color: perfConfig.color }}>
                {perfConfig.label}
              </Typography>
            </Box>
            {ruleScore != null && (
              <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                Auto Score: {Number(ruleScore).toFixed(1)} / {Number(totalMarks ?? 0).toFixed(1)}
              </Typography>
            )}
          </CardContent>
        </Card>
      </Box>

      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 2 }}>
        {aiOverall.strengths?.length > 0 && (
          <Card variant="outlined" sx={{ borderColor: "#bbf7d0" }}>
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
                <CheckIcon sx={{ color: "#16a34a", fontSize: 20 }} />
                <Typography variant="subtitle1" sx={{ fontWeight: 700, color: "#166534" }}>
                  Strengths
                </Typography>
              </Box>
              {aiOverall.strengths.map((s, i) => (
                <Box key={i} sx={{ display: "flex", alignItems: "flex-start", gap: 1, mb: 0.75 }}>
                  <Box sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: "#16a34a", mt: 0.7, flexShrink: 0 }} />
                  <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>{s}</Typography>
                </Box>
              ))}
            </CardContent>
          </Card>
        )}
        {aiOverall.weaknesses?.length > 0 && (
          <Card variant="outlined" sx={{ borderColor: "#fecaca" }}>
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
                <WrongIcon sx={{ color: "#dc2626", fontSize: 20 }} />
                <Typography variant="subtitle1" sx={{ fontWeight: 700, color: "#991b1b" }}>
                  Areas to Improve
                </Typography>
              </Box>
              {aiOverall.weaknesses.map((w, i) => (
                <Box key={i} sx={{ display: "flex", alignItems: "flex-start", gap: 1, mb: 0.75 }}>
                  <Box sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: "#dc2626", mt: 0.7, flexShrink: 0 }} />
                  <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>{w}</Typography>
                </Box>
              ))}
            </CardContent>
          </Card>
        )}
      </Box>

      {aiOverall.recommendations?.length > 0 && (
        <Card variant="outlined" sx={{ borderColor: "#bfdbfe" }}>
          <CardContent>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
              <HintIcon sx={{ color: "#2563eb", fontSize: 20 }} />
              <Typography variant="subtitle1" sx={{ fontWeight: 700, color: "#1e40af" }}>
                Study Recommendations
              </Typography>
            </Box>
            {aiOverall.recommendations.map((r, i) => (
              <Box key={i} sx={{ display: "flex", alignItems: "flex-start", gap: 1.5, mb: 1 }}>
                <Box sx={{ width: 22, height: 22, borderRadius: "50%", bgcolor: "#dbeafe", color: "#1e40af", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "0.75rem", fontWeight: 700, flexShrink: 0, mt: 0.1 }}>
                  {i + 1}
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>{r}</Typography>
              </Box>
            ))}
          </CardContent>
        </Card>
      )}
    </Box>
  );
};

const StudentPaperResult = () => {
  const { purchaseId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { isTestingMode } = useExamMode();
  const isPractice = isTestingMode("past_papers") || !!location.state?.isPractice;
  const alreadySubmitted = !!location.state?.alreadySubmitted;

  const [loading, setLoading] = useState(true);
  const [analytics, setAnalytics] = useState(null);
  const [tab, setTab] = useState("score");

  const [aiFeedbackStatus, setAiFeedbackStatus] = useState("pending");
  const [aiOverall, setAiOverall] = useState(null);
  const [aiScore, setAiScore] = useState(null);
  const [aiPercentage, setAiPercentage] = useState(null);
  const [questionFeedback, setQuestionFeedback] = useState({});
  const [breakdownPage, setBreakdownPage] = useState(0);
  const BREAKDOWN_PER_PAGE = 15;
  const pollRef = useRef(null);
  const pollCountRef = useRef(0);
  const MAX_POLLS = 20;

  useEffect(() => {
    const handleExamModeChanged = () => window.location.reload();
    window.addEventListener("exam-mode-changed", handleExamModeChanged);
    return () => window.removeEventListener("exam-mode-changed", handleExamModeChanged);
  }, []);

  const loadAnalytics = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/${getPastPaperApiPrefix()}/paper/${purchaseId}/analytics`);
      const data = res.data?.data ?? res.data;
      if (data?.score != null || data?.total_marks != null) {
        setAnalytics(data);
        if (data.ai_feedback_status) setAiFeedbackStatus(data.ai_feedback_status);
        if (data.ai_score != null) setAiScore(data.ai_score);
        setLoading(false);
        return;
      }
    } catch {
      // fall through to state fallback
    }

    const stateResults = location.state?.results;
    if (stateResults) {
      setAnalytics({
        paper_id: stateResults.paper_id ?? null,
        paper_name: stateResults.paper_name || "Paper",
        score: stateResults.score,
        total_marks: stateResults.total_marks,
        percentage: stateResults.percentage,
        total_questions: stateResults.total_questions ?? 0,
        top_level_count: stateResults.top_level_count ?? null,
        correct_questions: stateResults.correct_questions ?? 0,
        partial_questions: stateResults.partial_questions ?? 0,
        incorrect_questions: stateResults.incorrect_questions ?? 0,
        unanswered_questions: stateResults.unanswered_questions ?? 0,
        question_breakdown: stateResults.question_breakdown ?? [],
        ai_feedback_status: stateResults.ai_feedback_status ?? "pending",
        ai_score: stateResults.ai_score ?? null,
      });
      if (stateResults.ai_feedback_status) setAiFeedbackStatus(stateResults.ai_feedback_status);
      if (stateResults.ai_score != null) setAiScore(stateResults.ai_score);
    }
    setLoading(false);
  }, [purchaseId, location.state]);

  const fetchAiFeedback = useCallback(async () => {
    if (!purchaseId) return;
    try {
      const res = await api.get(`/${getPastPaperApiPrefix()}/paper/${purchaseId}/ai-feedback`);
      const payload = res.data?.data ?? res.data;
      const status = payload?.status ?? "pending";
      setAiFeedbackStatus(status);
      if (status === "completed") {
        setAiOverall(payload?.overall ?? null);
        setAiScore(payload?.ai_score ?? null);
        setAiPercentage(payload?.ai_percentage ?? null);
        setQuestionFeedback(payload?.question_feedback ?? {});
        clearInterval(pollRef.current);
        try {
          const res2 = await api.get(`/${getPastPaperApiPrefix()}/paper/${purchaseId}/analytics`);
          const data = res2.data?.data ?? res2.data;
          setAnalytics(data);
        } catch {
          // non-critical
        }
      } else if (status === "failed") {
        clearInterval(pollRef.current);
      }
    } catch {
      // silently ignore polling errors
    }
  }, [purchaseId]);

  useEffect(() => {
    loadAnalytics();
  }, [loadAnalytics]);

  useEffect(() => {
    if (!purchaseId) return;
    fetchAiFeedback();
    pollRef.current = setInterval(() => {
      pollCountRef.current += 1;
      if (pollCountRef.current >= MAX_POLLS) {
        clearInterval(pollRef.current);
        return;
      }
      fetchAiFeedback();
    }, 3000);
    return () => clearInterval(pollRef.current);
  }, [purchaseId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (aiFeedbackStatus === "completed" || aiFeedbackStatus === "failed") {
      clearInterval(pollRef.current);
    }
  }, [aiFeedbackStatus]);

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "50vh" }}>
        <CircularProgress size={48} />
      </Box>
    );
  }

  if (!analytics) {
    return (
      <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}>
        <Alert severity="error" sx={{ mb: 2 }}>Result not found.</Alert>
        <Button variant="contained" onClick={() => navigate(getPastPaperPortalHomePath())}>
          Back to Practice Papers
        </Button>
      </Box>
    );
  }

  const {
    paper_name, score: rawScore, total_marks, percentage: rawPct,
    total_questions, top_level_count, correct_questions, partial_questions,
    incorrect_questions, unanswered_questions,
    question_breakdown = [], paper_id,
  } = analytics;

  const displayTotalQuestions = top_level_count ?? total_questions;
  const hasSubQuestions = top_level_count != null && top_level_count !== total_questions;

  // The AI score REFINES the deterministic score — it never replaces it downward. Taking aiScore
  // whenever it is non-null let a failed/under-reporting AI pass (an AI score of 0 is `!= null`) wipe
  // marks the rule grader had already confirmed: a paper with a correct answer showed "AI Score 0.0 / 96,
  // Scored 0.0" while the breakdown row below it read "Correct, 1 mark". Floor the AI score at the rule
  // score so the two can never disagree in the student's favour being lost.
  const ruleScoreNum = Number(rawScore ?? 0);
  const effectiveScore = aiScore != null ? Math.max(Number(aiScore), ruleScoreNum) : ruleScoreNum;

  const pieData = [
    { name: "Correct",    value: correct_questions   ?? 0 },
    { name: "Partial",    value: partial_questions   ?? 0 },
    { name: "Incorrect",  value: incorrect_questions ?? 0 },
    { name: "Unanswered", value: unanswered_questions ?? 0 },
  ].filter((d) => d.value > 0);

  const verdictCtx = { aiFeedbackMap: questionFeedback, aiFeedbackStatus };
  const getPartVerdict = (p) => resolveLeafVerdict(p, verdictCtx);
  const getPartResult = (p) => getPartVerdict(p).result;
  const getPartMarks = (p) => getPartVerdict(p).earned;

  const reviewGroups = [];
  question_breakdown.forEach((q, i) => {
    const labelMatch = String(q.display_label ?? "").match(/^(\d+)([a-z]*)$/);
    const mainNum = labelMatch ? labelMatch[1] : String(i + 1);
    const partLetter = labelMatch ? labelMatch[2] : "";
    const last = reviewGroups[reviewGroups.length - 1];
    if (last && last.mainNum === mainNum) {
      last.parts.push({ ...q, partLetter });
    } else {
      reviewGroups.push({ mainNum, parts: [{ ...q, partLetter }] });
    }
  });

  const breakdownRows = [];
  reviewGroups.forEach((group) => {
    const isMultiPart = group.parts.length > 1 || !!group.parts[0]?.partLetter;
    group.parts.forEach((p, pi) => {
      breakdownRows.push({
        key: p.question_id ?? `${group.mainNum}-${pi}`,
        qLabel: pi === 0 ? `Q${group.mainNum}` : "",
        partLabel: isMultiPart ? (p.partLetter ? p.partLetter.toUpperCase() : "") : "",
        questionText: p.question_text,
        result: getPartResult(p),
        earned: Number(getPartMarks(p) ?? 0),
        max: Number(p.marks ?? 0),
        isMainRow: pi === 0,
      });
    });
  });

  // Score/percentage are derived from the same per-part marks the breakdown shows, so they stay on the
  // real question-mark basis the total now uses (fixed-mark groups counted at their full mark — 80, not
  // 53). Summing the parts is what lets a fully-correct paper read 80/80 instead of the stored per-part
  // grade (max 53). Floor at the stored rule score so AI/rule credit is never lost.
  const hasBreakdown = question_breakdown.length > 0;
  const partScored = reviewGroups.reduce(
    (s, g) => s + g.parts.reduce((ss, p) => ss + Number(getPartMarks(p) ?? 0), 0),
    0
  );
  const displayScore = hasBreakdown ? Math.max(partScored, ruleScoreNum) : effectiveScore;
  const displayPct = total_marks > 0
    ? Math.round((displayScore / total_marks) * 1000) / 10
    : Number(rawPct ?? 0);

  const barData = [{ name: "Performance", totalMarks: total_marks ?? 0, marksScored: displayScore }];

  return (
    <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}>
      <Button size="small" sx={{ mb: 2 }} onClick={() => navigate(getPastPaperPortalHomePath())}>
        Back to Practice Papers
      </Button>

      {isPractice && (
        <Alert severity="info" sx={{ mb: 2, borderRadius: 2, fontWeight: 600 }}>
          PRACTICE MODE — This is a practice attempt. Results are not recorded.
        </Alert>
      )}

      {alreadySubmitted && (
        <Alert severity="warning" sx={{ mb: 2, borderRadius: 2, fontWeight: 600 }}>
          You have already submitted this paper. Showing your saved results below.
        </Alert>
      )}

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.5 }}>
            {paper_name || "Paper"}
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 1.5 }}>
            Analytics Report
          </Typography>
          <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap", alignItems: "center" }}>
            {aiScore != null ? (
              <>
                <Tooltip title="AI-adjusted score (semantic matching)">
                  <Chip
                    icon={<AiIcon sx={{ fontSize: 16 }} />}
                    label={`AI Score: ${Number(displayScore).toFixed(1)} / ${Number(total_marks ?? 0).toFixed(1)}`}
                    color="secondary"
                    sx={{ fontWeight: 700 }}
                  />
                </Tooltip>
                <Chip label={`${Number(displayPct).toFixed(1)}%`} variant="outlined" color="secondary" />
              </>
            ) : (
              <>
                <Chip
                  label={`Score: ${Number(displayScore).toFixed(1)} / ${Number(total_marks ?? 0).toFixed(1)}`}
                  color="primary"
                />
                <Chip label={`${Number(displayPct).toFixed(1)}%`} variant="outlined" />
              </>
            )}
            {(aiFeedbackStatus === "pending" || aiFeedbackStatus === "processing") && (
              <Chip
                icon={<CircularProgress size={12} sx={{ color: "#7c3aed" }} />}
                label="AI feedback generating…"
                size="small"
                sx={{ bgcolor: "#f3f0ff", color: "#7c3aed", border: "1px solid #e0d7ff" }}
              />
            )}
          </Box>
        </CardContent>
      </Card>

      <Tabs
        value={tab}
        onChange={(_, v) => setTab(v)}
        variant="scrollable"
        scrollButtons="auto"
        allowScrollButtonsMobile
        sx={{ mb: 3 }}
      >
        <Tab value="score" label="Score" />
        <Tab value="marks" label="Marks Breakdown" />
        <Tab
          value="ai"
          label={
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <AiIcon sx={{ fontSize: 16 }} />
              AI Insights
              {(aiFeedbackStatus === "pending" || aiFeedbackStatus === "processing") && (
                <CircularProgress size={10} sx={{ ml: 0.5, color: "#7c3aed" }} />
              )}
            </Box>
          }
        />
      </Tabs>

      {tab === "score" && (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr 1fr" }, gap: 2 }}>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>Questions</Typography>
                {hasSubQuestions ? (
                  <>
                    <Typography variant="body2">Main questions: {displayTotalQuestions || "—"}</Typography>
                    <Typography variant="body2" sx={{ mb: 0.5 }}>Parts graded: {total_questions ?? 0}</Typography>
                  </>
                ) : (
                  <Typography variant="body2" sx={{ mb: 0.5 }}>Total: {displayTotalQuestions || "—"}</Typography>
                )}
                <Typography variant="body2" color="success.main">Correct: {correct_questions ?? 0}</Typography>
                {(partial_questions ?? 0) > 0 && (
                  <Typography variant="body2" sx={{ color: "#b45309" }}>Partial: {partial_questions}</Typography>
                )}
                <Typography variant="body2" color="error.main">Incorrect: {incorrect_questions ?? 0}</Typography>
                <Typography variant="body2" color="text.secondary">Unanswered: {unanswered_questions ?? 0}</Typography>
              </CardContent>
            </Card>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>Marks</Typography>
                <Typography variant="body2">Total: {total_marks != null ? fmtMarks(total_marks) : "—"}</Typography>
                <Typography variant="body2" color="success.main">
                  Scored: {fmtMarks(displayScore)}
                  {aiScore != null && (
                    <Typography component="span" variant="caption" sx={{ color: "#7c3aed", ml: 0.5 }}>(AI)</Typography>
                  )}
                </Typography>
              </CardContent>
            </Card>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>Summary</Typography>
                <Typography variant="body2">
                  Final Score: {fmtMarks(displayScore)}/{total_marks != null ? fmtMarks(total_marks) : "—"}
                  {aiScore != null && (
                    <Typography component="span" variant="caption" sx={{ color: "#7c3aed", ml: 0.5 }}>(AI-adjusted)</Typography>
                  )}
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  Percentage: {Number(displayPct).toFixed(1)}%
                </Typography>
              </CardContent>
            </Card>
          </Box>

          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 3 }}>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2 }}>Performance Overview</Typography>
                <ResponsiveContainer width="100%" height={250}>
                  <BarChart data={barData} margin={{ top: 10, right: 10, left: 10, bottom: 10 }}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                    <XAxis dataKey="name" tickLine={false} />
                    <YAxis tickLine={false} />
                    <RechartsTooltip />
                    <Legend />
                    <Bar dataKey="totalMarks" name="Total Marks" fill="#8884d8" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="marksScored" name={aiScore != null ? "AI Score" : "Marks Scored"} fill={aiScore != null ? "#7c3aed" : "#82ca9d"} radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
            {pieData.length > 0 && (
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2 }}>Question Analysis</Typography>
                  <ResponsiveContainer width="100%" height={250}>
                    <PieChart>
                      <Pie data={pieData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={80} label>
                        {pieData.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                      </Pie>
                      <RechartsTooltip />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            )}
          </Box>

          {question_breakdown.length > 0 && (() => {
            const totalPages = Math.ceil(reviewGroups.length / BREAKDOWN_PER_PAGE);
            const pagedGroups = reviewGroups.slice(breakdownPage * BREAKDOWN_PER_PAGE, (breakdownPage + 1) * BREAKDOWN_PER_PAGE);

            return (
              <Card variant="outlined">
                <CardContent>
                  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1.5, flexWrap: "wrap", gap: 1 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Question Breakdown</Typography>
                    {totalPages > 1 && (
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <Button size="small" variant="outlined" disabled={breakdownPage === 0}
                          onClick={() => setBreakdownPage((p) => p - 1)} sx={{ minWidth: 0, px: 1.5 }}>‹ Prev</Button>
                        <Typography variant="caption" color="text.secondary">
                          {breakdownPage + 1} / {totalPages}
                        </Typography>
                        <Button size="small" variant="outlined" disabled={breakdownPage >= totalPages - 1}
                          onClick={() => setBreakdownPage((p) => p + 1)} sx={{ minWidth: 0, px: 1.5 }}>Next ›</Button>
                      </Box>
                    )}
                  </Box>
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                    {pagedGroups.map((group) => {
                      const isMultiPart = group.parts.length > 1 || !!group.parts[0]?.partLetter;
                      const groupTotalMarks = group.parts.reduce((s, p) => s + Number(p.marks ?? 0), 0);
                      const groupEarned = group.parts.reduce((s, p) => s + Number(getPartMarks(p) ?? 0), 0);
                      const results = group.parts.map(getPartResult);
                      // The chip summarises the WHOLE question, so it has to follow the marks the rows
                      // below it already show. Reading the part verdicts as a set got this wrong in both
                      // directions: one wrong part painted the whole question red even when its siblings
                      // had earned marks ("Q31: one sub-part wrong → the entire part marked incorrect"),
                      // and a question answered correctly as far as the student got — the rest left blank
                      // — matched none of the branches and fell through to the grey "Written" default
                      // ("Q42: part (b) right, part (c) blank"). Earned-vs-total says both plainly.
                      const answeredResults = results.filter((r) => r !== "unanswered");
                      const groupResult = answeredResults.length === 0
                        ? "unanswered"
                        // Every answered part is still awaiting a written/AI read — no marks to report yet.
                        : answeredResults.every((r) => r === "written")
                          ? "written"
                          : results.every((r) => r === "correct") || (groupTotalMarks > 0 && groupEarned >= groupTotalMarks - 1e-9)
                            ? "correct"
                            : groupEarned > 0
                              ? "partial"
                              : "incorrect";
                      const allCorrect = groupResult === "correct";
                      const anyIncorrect = groupResult === "incorrect";
                      const summaryChipColor = { correct: "success", partial: "warning", incorrect: "error" }[groupResult] ?? "default";
                      const summaryChipLabel = { correct: "Correct", partial: "Partial", incorrect: "Incorrect", unanswered: "Not answered" }[groupResult] ?? "Written";
                      const previewText = (() => {
                        for (const part of group.parts) {
                          const t = truncateHtml(part.question_text, 90);
                          if (t.trim()) return t;
                        }
                        const hasFigure = group.parts.some((p) => p.image_url || (Array.isArray(p.image_urls) && p.image_urls.length));
                        return hasFigure ? "See figure" : null;
                      })();

                      return (
                        <Accordion key={group.mainNum} disableGutters elevation={0}
                          sx={{ border: "1px solid", borderColor: "divider", borderRadius: "8px !important", overflow: "hidden", "&:before": { display: "none" } }}>
                          <AccordionSummary expandIcon={<ExpandMoreIcon />}
                            sx={{ px: 2, minHeight: 52, "& .MuiAccordionSummary-content": { my: 0.75, alignItems: "center", gap: 1.5, flexWrap: "wrap", mr: 1 } }}>
                            <Typography sx={{ fontWeight: 700, minWidth: 34, color: "text.secondary", fontSize: "0.85rem" }}>
                              Q{group.mainNum}
                            </Typography>
                            <Box sx={{ flex: 1, minWidth: 0, overflow: "hidden", maxHeight: "2.4em",
                              fontSize: "0.875rem", lineHeight: 1.6, color: "text.primary",
                              "& .katex": { fontSize: "0.85em" },
                              // Only the block wrappers go inline: forcing it onto KaTeX's own spans
                              // collapsed a stacked fraction out of the clipped row (Dulwich 2025 I Q5).
                              "& p, & div": { display: "inline", m: 0 }, "& .katex-display": { display: "inline", m: 0 } }}>
                              {previewText
                                ? <HtmlWithLatex html={previewText} />
                                : <Typography component="span" variant="caption" color="text.disabled" sx={{ fontStyle: "italic" }}>expand to view</Typography>
                              }
                            </Box>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, flexShrink: 0 }}>
                              {isMultiPart && (
                                <Chip label={`${group.parts.length} parts`} size="small" variant="outlined"
                                  sx={{ fontSize: "0.68rem", height: 20, display: { xs: "none", sm: "inline-flex" } }} />
                              )}
                              <Chip
                                icon={allCorrect ? <CheckIcon sx={{ fontSize: 12 }} /> : anyIncorrect ? <WrongIcon sx={{ fontSize: 12 }} /> : undefined}
                                label={summaryChipLabel} size="small" color={summaryChipColor}
                                sx={{ fontWeight: 700, fontSize: "0.68rem", height: 22 }}
                              />
                              <Typography variant="caption" sx={{ fontWeight: 700, color: "text.secondary", whiteSpace: "nowrap", minWidth: 36, textAlign: "right" }}>
                                {fmtMarks(groupEarned)}/{fmtMarks(groupTotalMarks)}
                              </Typography>
                            </Box>
                          </AccordionSummary>
                          <AccordionDetails sx={{ p: 0, borderTop: "1px solid", borderColor: "divider" }}>
                            <Box>
                              {group.parts.map((p, pi) => {
                                const verdict = getPartVerdict(p);
                                const result = verdict.result;
                                const isEquiv = verdict.isEquiv;
                                // Only the resolver knows whether the AI verdict is what decided this row;
                                // "a feedback entry exists" is not the same thing and printed "AI
                                // Incorrect" over rows the rules graded.
                                const aiJudged = verdict.aiDecided;
                                return (
                                  <Box key={p.question_id ?? pi}
                                    sx={{ px: 2, py: 1.5, bgcolor: pi % 2 === 1 ? "#fafafa" : "transparent",
                                      borderBottom: pi < group.parts.length - 1 ? "1px solid" : "none", borderColor: "divider" }}>
                                    <Box sx={{ display: "grid", gap: 1,
                                      gridTemplateColumns: { xs: "auto 1fr", sm: "36px 1fr 1fr auto auto", md: "36px 1.2fr 0.9fr 0.9fr auto auto" },
                                      alignItems: "start" }}>
                                      {/* Part badge */}
                                      <Box sx={{ pt: 0.25 }}>
                                        {p.partLetter ? (
                                          <Chip label={p.partLetter.toUpperCase()} size="small" color="primary" variant="outlined"
                                            sx={{ fontWeight: 700, fontSize: "0.68rem", height: 22, minWidth: 28 }} />
                                        ) : (
                                          <Typography variant="caption" color="text.disabled">—</Typography>
                                        )}
                                      </Box>
                                      {/* Question */}
                                      <Box sx={{ gridColumn: { xs: "1 / -1", sm: "auto" } }}>
                                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: "block", mb: 0.25 }}>Question</Typography>
                                        <Typography variant="caption" sx={{ wordBreak: "break-word", lineHeight: 1.5 }}>
                                          <HtmlWithLatex html={truncateHtml(p.question_text, 120)} />
                                        </Typography>
                                      </Box>
                                      {/* Your Answer */}
                                      <Box>
                                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: "block", mb: 0.25 }}>Your Answer</Typography>
                                        <Box sx={{ fontSize: "0.75rem", wordBreak: "break-word" }}>
                                          <HtmlWithLatex html={formatAnswerLabel(p.your_answer) || "—"} />
                                        </Box>
                                        {p.your_answer_drawing && (
                                          <Box component="img" src={p.your_answer_drawing} alt="Submitted drawing"
                                            sx={{ mt: 0.5, maxWidth: 90, maxHeight: 60, borderRadius: 1, border: "1px solid", borderColor: "divider", display: "block" }} />
                                        )}
                                      </Box>
                                      {/* Correct Answer — hidden xs, shown sm+ */}
                                      <Box sx={{ display: { xs: "none", sm: "block" } }}>
                                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: "block", mb: 0.25 }}>Correct Answer</Typography>
                                        <Box sx={{ fontSize: "0.75rem", wordBreak: "break-word" }}>
                                          <HtmlWithLatex html={formatAnswerLabel(p.correct_answer_label) || "—"} />
                                        </Box>
                                      </Box>
                                      {/* Result */}
                                      <Box>
                                        {result === "unanswered" ? (
                                          <Chip label="Not answered" size="small" color="default" variant="outlined" sx={{ fontWeight: 600 }} />
                                        ) : result === "incorrect" ? (
                                          <Chip icon={aiJudged ? <AiIcon /> : <WrongIcon />}
                                            label={aiJudged ? "AI Incorrect" : "Incorrect"}
                                            size="small" color="error" sx={{ fontWeight: 700 }} />
                                        ) : result === "written" ? (
                                          <Chip icon={<HelpIcon />} label="Written" size="small" color="default" sx={{ fontWeight: 600 }} />
                                        ) : aiJudged && !p.is_correct ? (
                                          result === "correct"
                                            ? <Chip icon={<AiIcon />} label="AI Correct" size="small" color="success" sx={{ fontWeight: 700 }} />
                                            : <Chip icon={<AiIcon />} label="Partial" size="small" color="warning" sx={{ fontWeight: 700 }} />
                                        ) : result === "correct" ? (
                                          <Chip icon={<CheckIcon />} label={isEquiv ? "Equiv." : "Correct"} size="small" color="success" sx={{ fontWeight: 700 }} />
                                        ) : result === "partial" ? (
                                          <Chip label="Partial" size="small" sx={{ fontWeight: 700, bgcolor: "#fef3c7", color: "#92400e" }} />
                                        ) : (
                                          <Chip icon={<WrongIcon />} label="Incorrect" size="small" color="error" sx={{ fontWeight: 700 }} />
                                        )}
                                      </Box>
                                      {/* Marks */}
                                      <Box>
                                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: "block", mb: 0.25 }}>Marks</Typography>
                                        <Typography variant="caption" sx={{ fontWeight: 700 }}>
                                          {fmtMarks(getPartMarks(p))} / {fmtMarks(p.marks)}
                                        </Typography>
                                      </Box>
                                    </Box>
                                    {/* Correct answer on mobile */}
                                    {p.correct_answer_label && (
                                      <Box sx={{ display: { xs: "block", sm: "none" }, mt: 1, pl: 0 }}>
                                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>Correct: </Typography>
                                        <Typography variant="caption">
                                          <HtmlWithLatex html={formatAnswerLabel(p.correct_answer_label)} />
                                        </Typography>
                                      </Box>
                                    )}
                                  </Box>
                                );
                              })}
                            </Box>
                          </AccordionDetails>
                        </Accordion>
                      );
                    })}
                  </Box>
                  {totalPages > 1 && (
                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 1, mt: 2 }}>
                      <Button size="small" variant="outlined" disabled={breakdownPage === 0}
                        onClick={() => setBreakdownPage((p) => p - 1)} sx={{ minWidth: 0, px: 1.5 }}>‹ Prev</Button>
                      <Typography variant="caption" color="text.secondary">
                        Page {breakdownPage + 1} of {totalPages} · Q{breakdownPage * BREAKDOWN_PER_PAGE + 1}–Q{Math.min((breakdownPage + 1) * BREAKDOWN_PER_PAGE, reviewGroups.length)}
                      </Typography>
                      <Button size="small" variant="outlined" disabled={breakdownPage >= totalPages - 1}
                        onClick={() => setBreakdownPage((p) => p + 1)} sx={{ minWidth: 0, px: 1.5 }}>Next ›</Button>
                    </Box>
                  )}
                </CardContent>
              </Card>
            );
          })()}

          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            <Button
              variant="contained"
              size="large"
              startIcon={<RateReviewIcon />}
              onClick={() => navigate(getPastPaperResultPath(purchaseId, 'review'))}
              sx={{ textTransform: "none", fontWeight: 700, py: 1.2 }}
            >
              Review answers
            </Button>
            {paper_id && (
              <Button
                variant="outlined"
                size="large"
                onClick={() => navigate(`/${STUDENT_BASE}/past-paper-report/${paper_id}`)}
                sx={{ textTransform: "none", fontWeight: 600, py: 1.2 }}
              >
                All attempts
              </Button>
            )}
            <Button variant="outlined" onClick={() => navigate(getPastPaperPortalHomePath())} sx={{ textTransform: "none", fontWeight: 600 }}>
              Back to Practice Papers
            </Button>
          </Box>
        </Box>
      )}

      {tab === "marks" && (
        <MarksBreakdownTable
          rows={breakdownRows}
          totalMarks={total_marks ?? 0}
          scoredMarks={displayScore}
          title="Marks Breakdown"
          fileName={`${(paper_name || "paper").replace(/[^\w-]+/g, "_").slice(0, 60)}-marks`}
        />
      )}

      {tab === "ai" && (
        <AiInsightsTab
          aiFeedbackStatus={aiFeedbackStatus}
          aiOverall={aiOverall}
          aiScore={displayScore}
          aiPercentage={total_marks > 0 ? displayPct : aiPercentage}
          totalMarks={total_marks}
          ruleScore={analytics.raw_score ?? analytics.score}
        />
      )}
    </Box>
  );
};

export default StudentPaperResult;
